import { h, Fragment, Component, Prop, Element, Event, Watch, State } from '@stencil/core';
import type { ComponentInterface, EventEmitter } from '@stencil/core';
import { getBreakpoint, pointerHover } from 'utils';

const fullWidth = getBreakpoint('992px').up;

@Component({
	tag: 'app-details',
	styleUrl: 'details.scss',
	shadow: true
})
export class Details implements ComponentInterface {
	@Element() root: HTMLAppDetailsElement;

	/** Disables the toggle button */
	@Prop({ reflect: true }) disabled = false;
	/** Whether the details are open */
	@Prop({ mutable: true, reflect: true }) open = false;
	/** Name of the mutually-exclusive details group (accordion behavior) */
	@Prop({ reflect: true }) name?: string;
	/**
	 * If hovering is supported by the medium:
	 * - optionally visually hide the toggle button unless it is `:focus-visible` with `.hide-toggle`
	 * - details will be open during `:hover`
	 */
	@Prop({ reflect: true }) interestToggle = false;
	/**
	 * Shows the summary and the details while hiding the toggle and ignoring openness.
	 */
	@Prop({ reflect: true }) forceShow: 'all' | 'desktop' | 'mobile' | undefined;
	
	@State() fullWidth: boolean = fullWidth.matches;

	openedBy: undefined | 'interest-hover' | 'explicit-toggle';

	@Event() appDetailsToggled: EventEmitter<boolean>;
	@Watch('open')
	onOpenChange() {
		if (!this.open)
			this.openedBy = undefined;
		this.appDetailsToggled.emit(this.open);
	}

	componentWillLoad() {
		addEventListener('appDetailsToggled', (e: CustomEvent<boolean>) => {
			const other = e.composedPath()[0] as HTMLAppDetailsElement;
			if (other !== this.root && this.name && other.name === this.name && other.open)
				this.open = false;
		});

		addEventListener('keydown', ({ key }) => {
			if (key === 'Escape')
				this.open = false;
		});

		pointerHover(this.root, {
			onlyTrueHovers: true,
			onEachEnter: () => {
				if (!this.interestToggle)
					return false;

				if (!this.open)
					this.openedBy = 'interest-hover';
				this.open = true;
			},
			onAllLeave: () => {
				if (this.openedBy === 'interest-hover')
					this.open = false;
			}
		});

		fullWidth.addEventListener('change', () => {
			this.fullWidth = fullWidth.matches;
		});
	}

	render() {
		return <>
			<div id='summary' part='summary'>
				<slot name='summary' />

				<button
					id='toggle'
					part='toggle'
					type='button'
					aria-controls='content'
					aria-expanded={this.open + ''}
					onClick={() => {
						if (!this.open)
							this.openedBy = 'explicit-toggle';
						this.open = !this.open;
					}}
					disabled={this.disabled}
					hidden={(this.forceShow === 'all') ||
						(this.forceShow === 'desktop' && fullWidth.matches) || 
						(this.forceShow === 'mobile' && !fullWidth.matches)
					}
				>
					<slot name='toggle'>
						<slot name='toggle-label'>
							<slot name='open-label'>
								<span class='visually-hidden'>Open</span>
							</slot>
							<slot name='close-label'>
								<span class='visually-hidden'>Close</span>
							</slot>
						</slot>

						<slot name='toggle-icon'>
							<slot name='open-icon'>
								<app-icon part='toggle-icon open' icon='plus' />
							</slot>
							<slot name='close-icon'>
								<app-icon part='toggle-icon close' icon='minus' />
							</slot>
						</slot>
					</slot>
				</button>
			</div>

			<div
				id='content'
				part='content'
				hidden={!this.open && ( 
					!this.forceShow || 
					(this.forceShow === 'desktop' && !fullWidth.matches) || 
					(this.forceShow === 'mobile' && fullWidth.matches)
				)}
			>
				<slot />
			</div>
		</>;
	}
}
